/**
 * Author: zoesvision.com
 * Purpose: Lazily load Shopify product recommendations for the isolated Z section.
 */

(() => {
  class ZProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.zObserver || this.dataset.zLoaded === 'true') return;
      this.zAbortController = new AbortController();
      this.zObserver = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.zObserver.disconnect();
        this.zObserver = null;
        this.zLoad();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.zObserver.observe(this);
    }

    disconnectedCallback() {
      this.zObserver?.disconnect();
      this.zObserver = null;
      this.zAbortController?.abort();
      this.zAbortController = null;
    }

    async zLoad() {
      try {
        const response = await fetch(this.dataset.url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: this.zAbortController.signal
        });
        if (!response.ok) throw new Error(`Z product recommendations request failed: ${response.status}`);

        const documentFromResponse = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = Array.from(documentFromResponse.querySelectorAll('z-product-recommendations[data-z-section-id]'))
          .find((element) => element.dataset.zSectionId === this.dataset.zSectionId);

        if (!incoming?.querySelector('[data-z-recommendation-list]')) {
          this.classList.add('hidden');
          return;
        }

        this.innerHTML = incoming.innerHTML;
        // zoesvision.com | 2026-08-16 | Purpose: Observe recommendation cards inserted by the isolated async loader.
        this.querySelector('z-animate-on-scroll')?.refresh();
        this.dataset.zLoaded = 'true';
        this.classList.remove('hidden');
      } catch (error) {
        if (error.name !== 'AbortError') this.classList.add('hidden');
      } finally {
        this.zAbortController = null;
      }
    }
  }

  if (!customElements.get('z-product-recommendations')) {
    customElements.define('z-product-recommendations', ZProductRecommendations);
  }
})();
